/** Sign-in provider ids as they appear in `firebase.User.providerData`. */
export type SignInProviderId = 'password' | 'microsoft.com' | 'google.com';

export const PROVIDER_LABELS: Record<SignInProviderId, string> = {
    password: 'Email and password',
    'microsoft.com': 'Microsoft',
    'google.com': 'Google',
};

/** Error thrown by the link helpers: keeps the Firebase code and carries a plain-English message. */
export class AuthLinkError extends Error {
    constructor(
        public readonly code: string,
        message: string,
        /** True when the user simply closed the popup - callers should not show an error. */
        public readonly cancelled = false
    ) {
        super(message);
        this.name = 'AuthLinkError';
    }
}

/** Plain message for a Firebase link error, or null when it is a user cancel (show nothing). */
export function linkErrorMessage(code: string | undefined, providerLabel: string): string | null {
    switch (code) {
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
            return null;
        case 'auth/credential-already-in-use':
            return `That ${providerLabel} account already belongs to another Newgenus login. Use a different ${providerLabel} account.`;
        case 'auth/provider-already-linked':
            return `${providerLabel} is already connected to your account.`;
        case 'auth/popup-blocked':
            return `Your browser blocked the ${providerLabel} sign-in window. Allow pop-ups for this site and try again.`;
        case 'auth/requires-recent-login':
            return 'For security, sign out and back in, then try again.';
        case 'auth/no-such-provider':
            return `${providerLabel} is not connected to your account.`;
        default:
            return `Could not connect ${providerLabel}. Please try again.`;
    }
}

export function toAuthLinkError(err: any, providerLabel: string): AuthLinkError {
    const code = err?.code ?? 'unknown';
    const message = linkErrorMessage(code, providerLabel);
    return new AuthLinkError(code, message ?? 'Cancelled', message === null);
}

/** Provider ids currently attached to a user. */
export function linkedProviderIds(
    user: { providerData?: ({ providerId: string } | null)[] } | null | undefined
): string[] {
    return (user?.providerData ?? []).filter((p): p is { providerId: string } => !!p).map((p) => p.providerId);
}

/** Link a provider to a user; resolves on success, rejects with an AuthLinkError (never swallows). */
export async function linkProviderToUser(
    user: { linkWithPopup: (p: any) => Promise<unknown> } | null | undefined,
    provider: any,
    providerLabel: string
): Promise<void> {
    if (!user) {
        throw new AuthLinkError('auth/no-current-user', 'You are not signed in.');
    }
    try {
        await user.linkWithPopup(provider);
    } catch (err: unknown) {
        console.error(`${providerLabel} account linking failed`, (err as any)?.code);
        throw toAuthLinkError(err, providerLabel);
    }
}

/** Unlink a provider, refusing to remove the last remaining sign-in method. */
export async function unlinkProviderFromUser(
    user:
        | {
              providerData?: ({ providerId: string } | null)[];
              unlink: (id: string) => Promise<unknown>;
          }
        | null
        | undefined,
    providerId: string,
    providerLabel: string
): Promise<void> {
    if (!user) {
        throw new AuthLinkError('auth/no-current-user', 'You are not signed in.');
    }
    const ids = linkedProviderIds(user);
    if (!ids.includes(providerId)) {
        throw new AuthLinkError('auth/no-such-provider', linkErrorMessage('auth/no-such-provider', providerLabel)!);
    }
    if (ids.filter((i) => i !== providerId).length === 0) {
        throw new AuthLinkError(
            'auth/last-provider',
            'You need at least one sign-in method, so this one cannot be removed.'
        );
    }
    try {
        await user.unlink(providerId);
    } catch (err: unknown) {
        throw toAuthLinkError(err, providerLabel);
    }
}

/** What the Sign-in Methods card needs to render. */
export interface SignInMethodsView {
    password: boolean;
    microsoft: boolean;
    google: boolean;
    /** More than one method is linked, so any one of them may be unlinked. */
    canUnlink: boolean;
}

export function signInMethodsView(providerIds: string[]): SignInMethodsView {
    const known = providerIds.filter((i) => i === 'password' || i === 'microsoft.com' || i === 'google.com');
    return {
        password: known.includes('password'),
        microsoft: known.includes('microsoft.com'),
        google: known.includes('google.com'),
        canUnlink: providerIds.length > 1,
    };
}

/** The card's "Currently signed in with" text, e.g. "Email and password, Microsoft". */
export function signInSummary(view: SignInMethodsView): string {
    const parts = [
        view.password ? PROVIDER_LABELS.password : null,
        view.microsoft ? PROVIDER_LABELS['microsoft.com'] : null,
        view.google ? PROVIDER_LABELS['google.com'] : null,
    ].filter((p): p is string => !!p);
    return parts.length ? parts.join(', ') : 'No sign-in method found';
}
